import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import {
  Save,
  Loader2,
  RefreshCw,
  Plus,
  Trash2,
  Settings,
  Zap,
  Clock
} from 'lucide-react';
import costEstimationService from '../../services/costEstimationService';

const CostSettingsManager = ({ open, onClose }) => {
  const [settings, setSettings] = useState({
    labor_costs: { hourly_rate: 50 },
    electricity: { rate_per_kwh: 0.85, machine_power_kw: 5 },
    work_hours: { default_hours: 0.5, category_hours: {} }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', hours: '' });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await costEstimationService.getCostSettings();
      if (result.settings) {
        setSettings({
          labor_costs: result.settings.labor_costs || { hourly_rate: 50 },
          electricity: result.settings.electricity || { rate_per_kwh: 0.85, machine_power_kw: 5 },
          work_hours: result.settings.work_hours || { default_hours: 0.5, category_hours: {} }
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || '加载配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open, fetchSettings]);

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleSaveLaborCosts = async () => {
    setSaving(true);
    setError(null);
    try {
      await costEstimationService.updateCostSetting('labor_costs', settings.labor_costs);
      setSuccess('人工成本配置已保存');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveElectricity = async () => {
    setSaving(true);
    setError(null);
    try {
      await costEstimationService.updateCostSetting('electricity', settings.electricity);
      setSuccess('电费配置已保存');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefaultHours = async () => {
    setSaving(true);
    setError(null);
    try {
      await costEstimationService.updateCostSetting('work_hours', {
        default_hours: settings.work_hours.default_hours,
        category_hours: settings.work_hours.category_hours
      });
      setSuccess('默认工时已保存');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name || !newCategory.hours) {
      setError('请填写分类名称和工时');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await costEstimationService.updateWorkHoursCategory(
        newCategory.name,
        parseFloat(newCategory.hours)
      );
      setNewCategory({ name: '', hours: '' });
      await fetchSettings();
      setSuccess('分类工时已添加');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || '添加失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`确定要删除分类"${category}"的工时配置吗？`)) return;

    setSaving(true);
    setError(null);
    try {
      await costEstimationService.deleteWorkHoursCategory(category);
      await fetchSettings();
      setSuccess('分类工时已删除');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || '删除失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            成本核算配置
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchSettings}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 text-green-800 border-green-200">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              {/* Labor Costs */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    人工成本
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <Label htmlFor="hourly-rate">人工时薪 (元/小时)</Label>
                      <Input
                        id="hourly-rate"
                        type="number"
                        step="0.1"
                        value={settings.labor_costs.hourly_rate || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          labor_costs: { ...settings.labor_costs, hourly_rate: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                    <Button onClick={handleSaveLaborCosts} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      <span className="ml-2">保存</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Electricity */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    电费成本
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="rate-kwh">电费单价 (元/度)</Label>
                      <Input
                        id="rate-kwh"
                        type="number"
                        step="0.01"
                        value={settings.electricity.rate_per_kwh || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          electricity: { ...settings.electricity, rate_per_kwh: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="machine-power">设备功率 (千瓦)</Label>
                      <Input
                        id="machine-power"
                        type="number"
                        step="0.1"
                        value={settings.electricity.machine_power_kw || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          electricity: { ...settings.electricity, machine_power_kw: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                  </div>
                  <Button onClick={handleSaveElectricity} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    <span className="ml-2">保存</span>
                  </Button>
                </CardContent>
              </Card>

              {/* Work Hours */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    加工工时
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Default Hours */}
                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <Label htmlFor="default-hours">默认工时 (小时)</Label>
                      <Input
                        id="default-hours"
                        type="number"
                        step="0.1"
                        value={settings.work_hours.default_hours || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          work_hours: { ...settings.work_hours, default_hours: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                    <Button onClick={handleSaveDefaultHours} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      <span className="ml-2">保存</span>
                    </Button>
                  </div>

                  {/* Category Hours */}
                  <div>
                    <Label className="mb-2 block">分类工时配置</Label>
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>产品分类</TableHead>
                            <TableHead className="w-32 text-right">工时 (小时)</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(settings.work_hours.category_hours || {}).map(([category, hours]) => (
                            <TableRow key={category}>
                              <TableCell>{category}</TableCell>
                              <TableCell className="text-right">{hours}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCategory(category)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {Object.keys(settings.work_hours.category_hours || {}).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                                暂无分类工时配置
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Add Category */}
                    <div className="flex items-end gap-2 mt-3">
                      <div className="flex-1">
                        <Label htmlFor="new-category-name">分类名称</Label>
                        <Input
                          id="new-category-name"
                          value={newCategory.name}
                          onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                          placeholder="例: 钻头"
                        />
                      </div>
                      <div className="w-32">
                        <Label htmlFor="new-category-hours">工时</Label>
                        <Input
                          id="new-category-hours"
                          type="number"
                          step="0.1"
                          value={newCategory.hours}
                          onChange={(e) => setNewCategory({ ...newCategory, hours: e.target.value })}
                          placeholder="0.5"
                        />
                      </div>
                      <Button onClick={handleAddCategory} disabled={saving}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="secondary" onClick={handleClose}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CostSettingsManager;
